import type { FC } from 'react'

import { Button, InfoBox } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { useFormikContext } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { usePolicyId } from '@/modules/ecom-policy/policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'

import type { FormFields } from './types'

import type { Classification } from '../../new/types'

export const LeasingCompatibilityWarning: FC<{
  isReadOnlyView?: boolean
  leasingVendorCategories: string[]
  removedCategories: string[]
  leasingOrderIntervals: number[] | undefined
  onClose: () => void
  onCategoriesChange: (categories: string[]) => void
  onOrderIntervalChange: () => void
}> = ({
  isReadOnlyView,
  leasingVendorCategories,
  removedCategories,
  leasingOrderIntervals,
  onClose,
  onCategoriesChange,
  onOrderIntervalChange,
}) => {
  const { values, setFieldValue, setValues } = useFormikContext<FormFields>()
  const policyId = usePolicyId()
  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )

  const suggestedOrderInterval =
    leasingOrderIntervals && leasingOrderIntervals.length > 0
      ? Math.min(...leasingOrderIntervals)
      : undefined

  const handleDisableLeasing = () => {
    if (values.isLeasing) {
      setFieldValue('isLeasing', false)
    }
    onClose()
  }

  const handleProceed = () => {
    let updates: Partial<FormFields> = {}

    if (leasingVendorCategories.length > 0) {
      const selected = policy?.classificationList.split(',').map(c => c.trim()) || []
      const updatedSelected = selected.filter(c => leasingVendorCategories.includes(c))
      updates.classificationList = updatedSelected as Classification[]
      onCategoriesChange(leasingVendorCategories)
    }

    if (leasingOrderIntervals && suggestedOrderInterval !== undefined) {
      updates.orderInterval = suggestedOrderInterval
      onOrderIntervalChange()
    }

    if (Object.keys(updates).length > 0) {
      setValues({ ...values, ...updates })
    }

    onClose()
  }

  return (
    <div className="flex flex-col gap-4">
      {removedCategories.length > 0 && (
        <InfoBox emphasis="warning" className="flex flex-col items-start justify-start">
          <Translation id="This policy contains product categories that are incompatible with leasing. Should you choose to proceed, these categories will be removed. This may also affect the available and selected products." />
          <Translation id="The following categories will be removed:" className="mt-2" />
          <ul className="my-0 mt-2">
            {removedCategories.map(category => (
              <li key={category}>
                <Translation id={category} />
              </li>
            ))}
          </ul>
        </InfoBox>
      )}

      {leasingOrderIntervals && (
        <InfoBox emphasis="warning" className="flex flex-col items-start justify-start">
          <Translation id="The minimum time between orders is not supported by the chosen leasing vendor." />
          {suggestedOrderInterval !== undefined && (
            <div className="mt-2">
              <Translation
                id="This interval will be set to {interval} {interval, plural, one {month} other {months}}"
                interval={String(suggestedOrderInterval)}
              />
            </div>
          )}
        </InfoBox>
      )}

      {(isReadOnlyView || removedCategories.length > 0) && (
        <div>
          {isReadOnlyView ? (
            <Translation id="Please note that the classification list will be updated. Some categories and their associated products can be removed from this policy." />
          ) : removedCategories.length > 0 ? (
            <Translation id="Proceeding will apply the changes mentioned above." />
          ) : null}
        </div>
      )}

      <div className="flex w-full flex-row justify-between gap-4">
        {!isReadOnlyView && (
          <Button className="mx-0 w-1/2" onClick={handleDisableLeasing}>
            <Translation id="Cancel" />
          </Button>
        )}
        <Button variant="primary" className="mx-0 w-1/2" onClick={handleProceed}>
          <Translation id="Proceed" />
        </Button>
      </div>
    </div>
  )
}
